import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';

import { parsePhoneNumber } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import {
  DialogBodyComponent,
  DialogComponent,
  DialogHeaderComponent,
  NotificationsController,
  SpinnerComponent,
} from '@core/user-interface-legacy';

declare const hbspt: {
  forms: {
    create: (config: Record<string, unknown>) => void;
  };
};

@Component({
  selector: 'tlc-contact-sales-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DialogHeaderComponent, DialogBodyComponent, SpinnerComponent, TranslatePipe],
  templateUrl: './contact-sales-dialog.component.html',
  styleUrl: './contact-sales-dialog.component.less',
})
export class ContactSalesDialogComponent
  extends DialogComponent<void, void>
  implements AfterViewInit
{
  private static readonly PORTAL_ID = '2688240';
  private static readonly REGION = 'na1';
  private static readonly SCRIPT_SRC = '//js.hsforms.net/forms/embed/v2.js';

  private static readonly FORM_CSS = [
    '* { box-sizing: border-box; margin: 0; padding: 0; }',

    // Form base
    '.hs-form { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif; font-size: 14px; color: #3a3543; line-height: 1.5; }',

    // Fieldset / multi-column layout
    '.hs-form fieldset { max-width: 100% !important; border: none !important; padding: 0 !important; margin: 0 !important; }',
    '.hs-form fieldset.form-columns-1 .hs-form-field { width: 100% !important; }',
    '.hs-form fieldset.form-columns-2 { display: flex !important; gap: 8px; }',
    '.hs-form fieldset.form-columns-2 .hs-form-field { flex: 1 !important; width: auto !important; float: none !important; }',
    '.hs-form fieldset.form-columns-3 { display: flex !important; gap: 8px; }',
    '.hs-form fieldset.form-columns-3 .hs-form-field { flex: 1 !important; width: auto !important; float: none !important; }',

    // Field wrapper — consistent 12px spacing between all rows
    '.hs-form .hs-form-field { margin-bottom: 12px !important; }',
    '.hs-form .hs-form-field > label { display: block !important; font-size: 14px; font-weight: 500; color: #3a3543; margin-bottom: 4px !important; }',
    '.hs-form .hs-form-required { color: #d10015; }',
    '.hs-form .input { margin: 0 !important; }',

    // Inputs — width: 100% for most inputs, but NOT tel inputs (phone prefix uses natural width)
    '.hs-form select.hs-input, .hs-form input[type="text"].hs-input, .hs-form input[type="email"].hs-input, .hs-form input[type="number"].hs-input, .hs-form textarea.hs-input { width: 100% !important; }',
    '.hs-form .hs-input { box-sizing: border-box !important; height: 36px; padding: 0 8px !important; font-family: inherit; font-size: 14px; color: #3a3543; background: #fff; border: 1px solid #dcd8e4 !important; border-radius: 8px !important; outline: none; transition: border-color 0.15s, box-shadow 0.15s; }',
    '.hs-form .hs-input:hover { border-color: #c4bcc8 !important; }',
    '.hs-form .hs-input:focus { border-color: #5a1cb7 !important; box-shadow: 0 0 0 3px rgba(115,38,248,0.2) !important; }',
    '.hs-form .hs-input::placeholder { color: #b3afbb; }',
    ".hs-form select.hs-input { appearance: none; padding-right: 32px !important; background-image: url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%233a3543' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\"); background-repeat: no-repeat; background-position: right 10px center; }",
    '.hs-form textarea.hs-input { height: auto !important; min-height: 80px; padding: 8px !important; resize: vertical; }',

    // Phone field — the .hs-fieldtype-intl-phone wrapper has .hs-input class, reset input styling on it
    '.hs-form .hs-fieldtype-intl-phone { display: flex !important; flex-wrap: nowrap !important; gap: 8px !important; height: auto !important; padding: 0 !important; border: none !important; border-radius: 0 !important; background: transparent !important; }',
    '.hs-form .hs-fieldtype-intl-phone > select { flex: 0 1 auto !important; width: 140px !important; min-width: 0 !important; }',
    '.hs-form .hs-fieldtype-intl-phone > input[type="tel"] { flex: 1 1 0% !important; width: auto !important; min-width: 0 !important; }',

    // Phone layout
    '.hs-form fieldset { padding: 0 !important; margin: 0 !important; border: none !important; }',

    // Checkbox / radio
    '.hs-form ul.inputs-list { list-style: none !important; padding: 0 !important; margin: 4px 0 0 !important; }',
    '.hs-form ul.inputs-list li { display: block !important; margin: 0 0 6px !important; padding: 0 !important; width: 100% !important; }',
    '.hs-form ul.inputs-list li label { display: flex !important; align-items: center !important; gap: 8px !important; cursor: pointer; width: auto !important; float: none !important; text-align: left !important; justify-content: flex-start !important; }',
    '.hs-form ul.inputs-list li label input[type="checkbox"], .hs-form ul.inputs-list li label input[type="radio"] { appearance: none !important; -webkit-appearance: none !important; flex: 0 0 18px !important; width: 18px !important; height: 18px !important; margin: 0 !important; border: 2px solid #7e788b !important; border-radius: 4px !important; background: #fff; cursor: pointer; float: none !important; transition: background-color 0.2s cubic-bezier(0.42, 0, 0.58, 1), border-color 0.2s cubic-bezier(0.42, 0, 0.58, 1); }',
    ".hs-form ul.inputs-list li label input[type=\"checkbox\"]:checked { background-color: #5a1cb7 !important; border-color: #5a1cb7 !important; background-image: url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E\"); background-repeat: no-repeat; background-position: center; background-size: 12px; }",
    '.hs-form ul.inputs-list li label input[type="radio"] { border-radius: 50% !important; }',
    '.hs-form ul.inputs-list li label input[type="radio"]:checked { background-color: #5a1cb7 !important; border-color: #5a1cb7 !important; background-image: radial-gradient(circle, #fff 3px, transparent 3px); }',
    '.hs-form ul.inputs-list li label span { flex: 1 !important; font-size: 14px; color: #3a3543; text-align: left !important; }',

    // Error messages
    '.hs-form .hs-error-msgs { list-style: none !important; padding: 0 !important; margin: 4px 0 0 !important; }',
    '.hs-form .hs-error-msgs li { display: block !important; }',
    '.hs-form .hs-error-msgs label { font-size: 12px !important; color: #d10015 !important; font-weight: 400 !important; }',
    '.hs-form .hs_error_rollup .hs-error-msgs { margin-bottom: 12px !important; }',

    // Submit
    '.hs-form .hs-submit { margin-top: 20px !important; }',
    '.hs-form .hs-button { display: inline-flex !important; align-items: center; justify-content: center; width: 100% !important; height: 36px; padding: 0 16px !important; font-family: inherit; font-size: 14px; font-weight: 600; color: #fff !important; background-color: #5a1cb7 !important; border: 1px solid transparent !important; border-radius: 8px !important; cursor: pointer; transition: background-color 0.15s; }',
    '.hs-form .hs-button:hover { background-color: #481692 !important; }',
    '.hs-form .hs-button:active { background-color: #3f157f !important; }',

    // Legal consent
    '.hs-form .legal-consent-container { margin-top: 12px !important; font-size: 12px; color: #5d566c; line-height: 1.5; }',
    '.hs-form .legal-consent-container a { color: #5a1cb7; }',
    '.hs-form .legal-consent-container .input { border: none !important; }',

    // Hide empty/invisible field borders
    '.hs-form .hs-fieldtype-booleancheckbox .input { border: none !important; }',
  ].join('\n');

  private readonly i18n = inject(I18nService);
  private readonly document = inject(DOCUMENT);
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly notifications = inject(NotificationsController);
  private readonly formContainer = viewChild.required<ElementRef<HTMLDivElement>>('formContainer');

  private readonly formIds: Record<string, string> = {
    de: '0cf5f1d5-0302-44fe-abc8-5209582148ad',
    fr: 'b68000b9-d999-4213-a00d-368789af2375',
    nl: '6deb2f4e-4de7-4b8c-8387-c8bfb2ec2068',
  };
  private readonly defaultFormId = '64305cf8-c8ca-443d-b5d7-6b54a0930f3e';

  protected readonly loading = signal(true);
  protected readonly formError = signal(false);

  public ngAfterViewInit(): void {
    this.clearHubSpotCache();
    this.loadScript()
      .then(() => this.createForm())
      .catch(() => this.handleFormError());
  }

  private createForm(): void {
    const locale = this.i18n.locale;
    const formId = this.formIds[locale] ?? this.defaultFormId;
    const container = this.formContainer().nativeElement;

    container.id = `hs-form-${Date.now()}`;
    try {
      hbspt.forms.create({
        region: ContactSalesDialogComponent.REGION,
        portalId: ContactSalesDialogComponent.PORTAL_ID,
        formId,
        target: `#${container.id}`,
        cssRequired: ContactSalesDialogComponent.FORM_CSS,
        inlineMessage: ' ',
        onFormReady: ($form: unknown) => {
          this.loading.set(false);
          setTimeout(() => this.prefillForm($form), 500);
        },
        onFormSubmitted: () => {
          this.dialogRef.close();
          this.notifications.success(
            this.i18n.translate('app.support_fab.contact_sales_submitted'),
          );
        },
      });
    } catch {
      this.handleFormError();
    }
  }

  private handleFormError(): void {
    this.loading.set(false);
    this.formError.set(true);
  }

  private clearHubSpotCache(): void {
    const isHubSpotKey = (key: string): boolean =>
      /hubspot|leadin|hs[-_]form|hs_submissions/i.test(key) ||
      key.includes(ContactSalesDialogComponent.PORTAL_ID);

    for (const storage of [localStorage, sessionStorage]) {
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i);
          if (key && isHubSpotKey(key)) {
            keysToRemove.push(key);
          }
        }
        for (const key of keysToRemove) {
          storage.removeItem(key);
        }
      } catch {
        // Storage may be unavailable in some environments
      }
    }

    this.document.cookie = 'hubspotutk=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  private prefillForm($form: unknown): void {
    const formEl = (<{ 0?: HTMLElement }>$form)?.[0] ?? <HTMLElement>$form;
    if (!(formEl instanceof HTMLElement)) return;

    const user = this.currentUserFacade.currentUser();
    if (!user) return;

    const container = this.formContainer().nativeElement;
    const query = <T extends Element>(selector: string): T | null =>
      formEl.querySelector<T>(selector) ?? container.querySelector<T>(selector);

    const fields: Record<string, string | undefined> = {
      firstname: user.firstName,
      lastname: user.lastName,
      company: this.currentUserFacade.currentOrg()?.name,
    };

    for (const [name, value] of Object.entries(fields)) {
      if (!value) continue;
      const input = query<HTMLInputElement>(`input[name="${name}"]`);
      if (input) {
        this.setInputValue(input, value);
      }
    }

    if (user.email) {
      const emailInput =
        query<HTMLInputElement>('input[name="email"]') ??
        query<HTMLInputElement>('input[name="work_email"]') ??
        query<HTMLInputElement>('input[type="email"]');
      if (emailInput) {
        this.setInputValue(emailInput, user.email);
      }
    }

    if (user.phone) {
      this.prefillPhone(query, user.phone);
    }
  }

  private prefillPhone(
    query: <T extends Element>(selector: string) => T | null,
    phone: string,
  ): void {
    try {
      const parsed = parsePhoneNumber(phone);
      if (parsed) {
        const dialCode = `+${parsed.countryCallingCode}`;
        const nationalNumber = parsed.nationalNumber;

        // Option values are ISO country codes (e.g. "PL", "DE")
        const countrySelect =
          query<HTMLSelectElement>('select[name="phone_ext"]') ??
          query<HTMLSelectElement>('select[id^="phone_ext"]');
        if (countrySelect && parsed.country) {
          countrySelect.value = parsed.country;
          countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // The visible tel input serves as both prefix display and number entry;
        // set it to the full number with dial code after the country change settles
        setTimeout(() => {
          const phoneInput = query<HTMLInputElement>('.hs-fieldtype-intl-phone input[type="tel"]');
          if (phoneInput) {
            this.setInputValue(phoneInput, `${dialCode} ${nationalNumber}`);
          }
        }, 500);
        return;
      }
    } catch {
      // Parsing failed (e.g. no country code prefix) — fall through
    }

    const phoneInput = query<HTMLInputElement>('.hs-fieldtype-intl-phone input[type="tel"]');
    if (phoneInput) {
      this.setInputValue(phoneInput, phone);
    }
  }

  private setInputValue(input: HTMLInputElement, value: string): void {
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  private loadScript(): Promise<void> {
    if (typeof hbspt !== 'undefined' && hbspt?.forms) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => reject(new Error('Script load timeout')), 15_000);

      const onLoad = (): void => {
        clearTimeout(timeoutId);
        resolve();
      };
      const onError = (): void => {
        clearTimeout(timeoutId);
        reject(new Error('Script failed to load'));
      };

      const existingScript = this.document.querySelector<HTMLScriptElement>(
        `script[src="${ContactSalesDialogComponent.SCRIPT_SRC}"]`,
      );

      if (existingScript) {
        existingScript.addEventListener('load', onLoad);
        existingScript.addEventListener('error', onError);
        return;
      }

      const script = this.document.createElement('script');
      script.src = ContactSalesDialogComponent.SCRIPT_SRC;
      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);
      this.document.body.appendChild(script);
    });
  }
}
